import { useState, useEffect } from 'react';
import { addPassToUser } from '../../../services/adminService';
import { getPassTypes } from '../../../services/passService';
import { useAppContext } from '../../../context/AppContext';
import { finalPriceLabel, passCurrencySymbol } from '../../../utils/currencyFormat';

// When opened as the "no active pass" fallback from AddStudentModal, classDetails is passed
// so we can preselect the cheapest single (drop-in) pass type valid for this class's classType,
// defaulting to unpaid with a short 2-day expiry - the admin just confirms or adjusts before saving.
export default function AddPassModal({ userId, classDetails, onClose, onSuccess }) {
  const { setToaster, token } = useAppContext();
  const [passTypes, setPassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [matchedForClass, setMatchedForClass] = useState(false);
  const [newPass, setNewPass] = useState({
    passTypeId: '',
    daysValid: 30,
    paid: true,
    notes: '',
    isAdmin: false,
    finalPrice: '',
  });

  useEffect(() => {
    fetchPassTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classTypeIdOf = (v) => (v ? String(v?._id || v) : null);

  const getActiveDiscount = (passType) => {
    const now = new Date();
    return passType.discounts?.find(
      (d) => d.active && new Date(d.validFrom) <= now && new Date(d.validTo) >= now
    );
  };

  const getDiscountedPrice = (passType) => {
    const discount = getActiveDiscount(passType);
    if (!discount) return passType.basePrice;
    return Math.round(passType.basePrice * (1 - discount.discountPercent / 100));
  };

  const fetchPassTypes = async () => {
    try {
      const data = await getPassTypes({ all: true });
      const types = Array.isArray(data) ? data : [];
      setPassTypes(types);

      const targetClassTypeId = classTypeIdOf(classDetails?.classType);
      if (targetClassTypeId) {
        const candidates = types.filter((pt) =>
          pt.active !== false &&
          !pt.testOnly &&
          pt.classesIncluded === 1 &&
          (pt.allowedClassTypes || []).some((ct) => classTypeIdOf(ct) === targetClassTypeId)
        );
        if (candidates.length) {
          const match = candidates.reduce((cheapest, pt) =>
            getDiscountedPrice(pt) < getDiscountedPrice(cheapest) ? pt : cheapest
          );
          setNewPass((prev) => ({
            ...prev,
            passTypeId: match._id,
            daysValid: 2,
            paid: false,
            finalPrice: String(getDiscountedPrice(match)),
          }));
          setMatchedForClass(true);
        }
      }
    } catch (error) {
      setToaster({
        message: "Failed to load pass types",
        type: "error",
        show: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedPassType = () => {
    return passTypes.find(pt => pt._id === newPass.passTypeId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPass.passTypeId) {
      setToaster({
        message: "Please select a pass type",
        type: "error",
        show: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = { userId, ...newPass };
      const priceNum = newPass.finalPrice.trim() === '' ? undefined : Number(newPass.finalPrice);
      if (typeof priceNum === 'number' && !Number.isNaN(priceNum) && priceNum >= 0) {
        payload.finalPrice = priceNum;
      }
      await addPassToUser(token, payload);
      setToaster({
        message: "Pass added successfully!",
        type: "success",
        show: true,
      });
      onSuccess();
    } catch (error) {
      setToaster({
        message: "Failed to add pass",
        type: "error",
        show: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <p className="text-center text-gray-500">Loading pass types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">Add pass</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 min-h-0 px-6 py-4 space-y-5">
          {matchedForClass && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              Preselected the cheapest single pass for this class — unpaid, valid 2 days. Adjust if needed.
            </p>
          )}
          {/* Pass type & validity */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pass type *</label>
              <select
                value={newPass.passTypeId}
                onChange={(e) => {
                  const selectedPt = passTypes.find(pt => pt._id === e.target.value);
                  const defaultPrice = selectedPt ? String(getDiscountedPrice(selectedPt)) : '';
                  setNewPass({
                    ...newPass,
                    passTypeId: e.target.value,
                    daysValid: selectedPt?.daysValid || 30,
                    finalPrice: defaultPrice,
                  });
                }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown"
                required
              >
                <option value="">Select a pass type</option>
                {passTypes.map((pt) => {
                  const discount = getActiveDiscount(pt);
                  const discountedPrice = getDiscountedPrice(pt);
                  const hasDiscount = discount && discountedPrice < pt.basePrice;
                  return (
                    <option key={pt._id} value={pt._id}>
                      {pt.name} — {pt.classesIncluded || '∞'} classes —
                      {hasDiscount
                        ? ` ${discountedPrice} ${pt.currency || 'ILS'} (was ${pt.basePrice})`
                        : ` ${pt.basePrice} ${pt.currency || 'ILS'}`
                      }
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid days</label>
              <input
                type="number"
                value={newPass.daysValid}
                onChange={(e) => setNewPass({ ...newPass, daysValid: parseInt(e.target.value) || 30 })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown"
                min="1"
              />
              {getSelectedPassType() && (
                <p className="text-xs text-gray-500 mt-1">
                  Default: {getSelectedPassType().daysValid || 30} days
                </p>
              )}
            </div>
            {getSelectedPassType() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Final price (₪)</label>
                <input
                  type="number"
                  value={newPass.finalPrice}
                  onChange={(e) => setNewPass({ ...newPass, finalPrice: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown"
                  min="0"
                  step="1"
                  placeholder={String(getDiscountedPrice(getSelectedPassType()))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default: {getDiscountedPrice(getSelectedPassType())}{' '}
                  {passCurrencySymbol(getSelectedPassType()?.currency)}
                </p>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="add-pass-is-admin"
                checked={newPass.isAdmin === true}
                onChange={(e) => setNewPass({ ...newPass, isAdmin: e.target.checked })}
                className="rounded border-gray-300 text-brown focus:ring-brown w-4 h-4"
              />
              <label htmlFor="add-pass-is-admin" className="text-sm font-medium text-gray-700">
                Test pass (admin)
              </label>
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Payment</span>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="paid"
                    checked={newPass.paid === true}
                    onChange={() => setNewPass({ ...newPass, paid: true })}
                    className="w-4 h-4 text-brown focus:ring-brown"
                  />
                  <span className="text-sm text-gray-700">Paid</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="paid"
                    checked={newPass.paid === false}
                    onChange={() => setNewPass({ ...newPass, paid: false })}
                    className="w-4 h-4 text-brown focus:ring-brown"
                  />
                  <span className="text-sm text-gray-700">Not paid</span>
                </label>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={newPass.notes}
              onChange={(e) => setNewPass({ ...newPass, notes: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brown/20 focus:border-brown resize-none"
              rows={2}
              placeholder="Optional..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-center md:justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2.5 bg-brown text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Adding…' : 'Add pass'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
